import { useEffect, useRef } from "react";
import {
  isPushSupported,
  isNotificationPermitted,
  requestNotificationPermission,
  registerServiceWorker,
  subscribeToPush
} from "@/utils/notifications";

/**
 * Hook that manages push notification subscription lifecycle.
 * - On login: registers SW, requests permission, subscribes
 * - Shows browser permission prompt once per session
 */
const usePushNotifications = () => {
  const attemptedRef = useRef(false);

  useEffect(() => {
    const initPush = async () => {
      if (attemptedRef.current) return;
      
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) return;
      
      if (!isPushSupported()) return;
      
      attemptedRef.current = true;

      try {
        // Register service worker first
        await registerServiceWorker();

        // If already permitted, just subscribe
        if (isNotificationPermitted()) {
          await subscribeToPush(token);
          return;
        }

        // Request permission (shows browser prompt)
        const granted = await requestNotificationPermission();
        if (granted) {
          await subscribeToPush(token);
        }
      } catch (err) {
        console.error("Push notification setup failed:", err);
      }
    };

    // Run on mount if user is already logged in
    initPush();

    // Also run when user logs in during this session
    const handleLogin = () => {
      attemptedRef.current = false;
      initPush();
    };

    window.addEventListener("userLogin", handleLogin);
    return () => window.removeEventListener("userLogin", handleLogin);
  }, []);
};

export default usePushNotifications;
